import { ExternalClaimSubmit, FullPageError, Settings } from "pages";
import { Navigate, redirect, redirectDocument } from "react-router-dom";

import { BillPay } from "components/bill-pay/bill-pay";
import { Feature } from "components/feature";
import { PaperlessEnrollment } from "components/paperless-enrollment";
import AuthAppWrapper from "components/root/auth-app-wrapper";
import { Billing } from "pages/billing";
import { Claims } from "pages/claims";
import { ContactUs } from "pages/contact-us";
import { Documents } from "pages/documents";
import { EmbeddedDocusign } from "pages/documents/embedded-docusign";
import { IneligibleOffer } from "pages/ineligible-offer";
import { Inspections } from "pages/inspections";
import { InspectionsOld } from "pages/inspections/inspections-old";
import { Offer } from "pages/offer";
import { LaundryRoomBlog } from "pages/offer/affiliate-blog/laundry-room-blog-post";
import { ContactInformation as ContactInformationTing } from "pages/offer/ting/contact-information";
import { ContactInformationConfirmation as ContactInformationConfirmationTing } from "pages/offer/ting/contact-information-confirmation";
import { PolicyConfirmation as PolicyConfirmationTing } from "pages/offer/ting/policy-confirmation";
import { TingEnrolled } from "pages/offer/ting/ting-enrolled";
import { TingFlow } from "pages/offer/ting/ting-flow";
import { TingLanding } from "pages/offer/ting/ting-landing";
import { ContactInformation } from "pages/offer/wind-defense/contact-information";
import { ContactInformationConfirmation } from "pages/offer/wind-defense/contact-information-confirmation";
import { Enrolled } from "pages/offer/wind-defense/enrolled";
import { PolicyConfirmation } from "pages/offer/wind-defense/policy-confirmation";
import { ShingleResealLanding } from "pages/offer/wind-defense/shingle-reseal-landing";
import { WaiverLiftlock } from "pages/offer/wind-defense/waiver-liftlock";
import { WaiverSageSure } from "pages/offer/wind-defense/waiver-sagesure";
import { WindDefenseFlow } from "pages/offer/wind-defense/wind-defense-flow";
import Overview from "pages/overview";
import { PaymentPlanUpdate } from "pages/payment-plan-update";
import { Policy } from "pages/policy";
import PropertyProfile from "pages/property-profile";
import { PropertyUpdateCompleted } from "pages/property-profile/property-update-completed";
import { PropertyUpdatePoolFlow } from "pages/property-profile/property-update-pool";
import { PoolBoardSlide } from "pages/property-profile/property-update-pool/pool-board-slide";
import { PoolConfirmation } from "pages/property-profile/property-update-pool/pool-confirmation";
import { PoolDecking } from "pages/property-profile/property-update-pool/pool-decking";
import { PoolFence } from "pages/property-profile/property-update-pool/pool-fence";
import { PoolFilled } from "pages/property-profile/property-update-pool/pool-filled";
import { PoolProofUpload } from "pages/property-profile/property-update-pool/pool-proof-upload";
import { PoolReview } from "pages/property-profile/property-update-pool/pool-review";
import { PropertyPoolType } from "pages/property-profile/property-update-pool/pool-type";
import { PropertyUpdateRoofFlow } from "pages/property-profile/property-update-roof";
import { RoofConstructionYear } from "pages/property-profile/property-update-roof/roof-construction-year";
import { RoofMaterial } from "pages/property-profile/property-update-roof/roof-material";
import { RoofProofUpload } from "pages/property-profile/property-update-roof/roof-proof-upload";
import { RoofReview } from "pages/property-profile/property-update-roof/roof-review";
import { PropertyUpdateSecurityFlow } from "pages/property-profile/property-update-security";
import { SecurityBurglarAlarm } from "pages/property-profile/property-update-security/security-burglar-alarm";
import { SecurityFireAlarm } from "pages/property-profile/property-update-security/security-fire-alarm";
import { SecurityReview } from "pages/property-profile/property-update-security/security-review";
import { PropertyUpdateTrampolineFlow } from "pages/property-profile/property-update-trampoline";
import { TrampolineConfirmation } from "pages/property-profile/property-update-trampoline/trampoline-confirmation";
import { TrampolineOnProperty } from "pages/property-profile/property-update-trampoline/trampoline-on-property";
import { TrampolinePadding } from "pages/property-profile/property-update-trampoline/trampoline-padding";
import { TrampolineProof } from "pages/property-profile/property-update-trampoline/trampoline-proof-upload";
import { TrampolineReview } from "pages/property-profile/property-update-trampoline/trampoline-review";
import UpdatePool, { UpdatePoolForm } from "pages/property-profile/update-pool";
import Contact from "pages/settings/contact";
import Offers from "pages/settings/offers";
import { Paperless } from "pages/settings/paperless";
import { Payment } from "pages/settings/payment";
import { Policies } from "pages/settings/policies";
import Security from "pages/settings/security";
import { ShingleRepairLanding } from "pages/shingle-repair/shingle-repair-landing";
import { Support } from "pages/support";
import {
  isLoggedIn,
  separateRedirectParams,
  setCookie,
  setMixpanelSuperProperties,
  trackEvent,
  urlSearchParamsObject,
  wrappedBrowserRouter,
} from "utils";

import { Main } from "./main";

// Setup public routes
const publicPages = [
  {
    path: "",
    async loader() {
      if (isLoggedIn()) {
        return redirect("/my/overview");
      }
      return null;
    },
    async lazy() {
      const LoginPage = await import("pages/login-page");
      return { Component: LoginPage.default };
    },
  },
  { path: "payment", element: <BillPay /> },
  {
    path: "ceremony/*",
    async lazy() {
      const CeremonyPage = await import("pages/ceremony");
      return { Component: CeremonyPage.default };
    },
  },
  {
    path: "request-reset",
    async lazy() {
      const RequestResetPage = await import("pages/request-reset");
      return { Component: RequestResetPage.default };
    },
  },
  {
    path: "resend-verification-email",
    async lazy() {
      const ResendVerificationPage =
        await import("pages/resend-verification-email");
      return { Component: ResendVerificationPage.default };
    },
  },
  {
    path: "easypay_confirmation",
    async loader() {
      const { event } = urlSearchParamsObject();
      if (isLoggedIn()) {
        if (event === "signing_complete") {
          // This will hide any documents to sign for five minutes
          // If there are documents on multiple policies they'll all be hidden
          // But, we don't have a policy number to restrict it to, so...
          const FIVE_MIN = 300;
          setCookie("hide-documents-to-sign", true, FIVE_MIN);
          return redirect("/my/documents?documents_signed=true");
        } else {
          return redirect("/my/documents");
        }
      }
      return null;
    },
    async lazy() {
      const { DocumentSigned } = await import("pages/document-signed");
      return { Component: DocumentSigned };
    },
  },
  { path: "paperless-enrollment", element: <PaperlessEnrollment /> },
  {
    path: "terms-of-use",
    async lazy() {
      const TermsPage = await import("pages/terms");
      return { Component: TermsPage.default };
    },
  },
  {
    path: "privacy-policy",
    async lazy() {
      const PrivacyPage = await import("pages/privacy");
      return { Component: PrivacyPage.default };
    },
  },
  {
    path: "contact-us",
    element: <ContactUs />,
  },
  {
    path: "contact-us",
    element: <ContactUs />,
  },
  {
    path: "shingle-landing",
    element: <ShingleRepairLanding />,
  },
];

// Setup private routes
const privatePages = [
  {
    path: "my/*",
    loader: privateLoader,
    id: "private",
    element: <AuthAppWrapper />,
    children: [
      { path: "*", element: <Navigate to="/" replace /> },
      {
        path: "policies",
        element: <Navigate to="/my/policies/:urlPolicyId" replace />,
      },
      { path: "overview", element: <Overview /> },
      { path: "billing/update", element: <PaymentPlanUpdate /> },
      { path: "billing", element: <Billing /> },
      { path: "resources/laundry-room", element: <LaundryRoomBlog /> },
      {
        path: "documents/sign/:envelopeId",
        element: <EmbeddedDocusign />,
      },
      {
        path: "documents",
        element: <Navigate to="/my/policies/:urlPolicyId/documents" replace />,
      },
      { path: "policies/:urlPolicyId/documents", element: <Documents /> },
      { path: "policies/:urlPolicyId", element: <Policy /> },
      {
        path: "property",
        element: <PropertyProfile />,
      },
      {
        path: "property/roof/*",
        element: <PropertyUpdateRoofFlow />,
        children: [
          {
            element: <RoofConstructionYear />,
            index: true,
          },
          {
            path: "material",
            element: <RoofMaterial />,
          },
          {
            path: "proof",
            element: <RoofProofUpload />,
          },
          {
            path: "review",
            element: <RoofReview />,
          },
          {
            path: "success",
            element: <PropertyUpdateCompleted />,
          },
        ],
      },
      {
        path: "property/security/*",
        element: <PropertyUpdateSecurityFlow />,
        children: [
          {
            element: <SecurityBurglarAlarm />,
            index: true,
          },
          {
            path: "fire-alarm",
            element: <SecurityFireAlarm />,
          },
          {
            path: "review",
            element: <SecurityReview />,
          },
          {
            path: "success",
            element: <PropertyUpdateCompleted />,
          },
        ],
      },
      {
        path: "property/trampoline/*",
        element: <PropertyUpdateTrampolineFlow />,
        children: [
          {
            index: true,
            element: <TrampolineOnProperty />,
          },
          {
            path: "padding",
            element: <TrampolinePadding />,
          },
          {
            path: "proof",
            element: <TrampolineProof />,
          },
          {
            path: "confirm",
            element: <TrampolineConfirmation />,
          },
          {
            path: "review",
            element: <TrampolineReview />,
          },
          {
            path: "success",
            element: <PropertyUpdateCompleted />,
          },
        ],
      },
      {
        path: "property/pool/*",
        element: (
          <Feature flag="poolUpdateFlow" fallback={() => <UpdatePool />}>
            <PropertyUpdatePoolFlow />
          </Feature>
        ),
        children: [
          {
            element: (
              <Feature
                flag="poolUpdateFlow"
                fallback={() => <UpdatePoolForm />}
              >
                <PropertyPoolType />
              </Feature>
            ),
            index: true,
          },
          {
            path: "fence",
            element: (
              <Feature
                flag="poolUpdateFlow"
                fallback={() => <Navigate to="/my/property/pool" />}
              >
                <PoolFence />
              </Feature>
            ),
          },
          {
            path: "board-slide",
            element: (
              <Feature
                flag="poolUpdateFlow"
                fallback={() => <Navigate to="/my/property/pool" />}
              >
                <PoolBoardSlide />
              </Feature>
            ),
          },
          {
            path: "filled",
            element: (
              <Feature
                flag="poolUpdateFlow"
                fallback={() => <Navigate to="/my/property/pool" />}
              >
                <PoolFilled />
              </Feature>
            ),
          },
          {
            path: "decking",
            element: (
              <Feature
                flag="poolUpdateFlow"
                fallback={() => <Navigate to="/my/property/pool" />}
              >
                <PoolDecking />
              </Feature>
            ),
          },
          {
            path: "proof",
            element: (
              <Feature
                flag="poolUpdateFlow"
                fallback={() => <Navigate to="/my/property/pool" />}
              >
                <PoolProofUpload />
              </Feature>
            ),
          },
          {
            path: "confirm",
            element: (
              <Feature
                flag="poolUpdateFlow"
                fallback={() => <Navigate to="/my/property/pool" />}
              >
                <PoolConfirmation />
              </Feature>
            ),
          },
          {
            path: "review",
            element: (
              <Feature
                flag="poolUpdateFlow"
                fallback={() => <Navigate to="/my/property/pool" />}
              >
                <PoolReview />
              </Feature>
            ),
          },
          {
            path: "success",
            element: (
              <Feature
                flag="poolUpdateFlow"
                fallback={() => <Navigate to="/my/property/pool" />}
              >
                <PropertyUpdateCompleted />
              </Feature>
            ),
          },
        ],
      },
      {
        path: "claims/submit",
        element: <ExternalClaimSubmit />,
      },
      { path: "claims", element: <Claims /> },
      { path: "support", element: <Support /> },
      {
        path: "inspections",
        element: (
          <Feature
            flag="inspectionsProofUpload"
            fallback={() => <InspectionsOld />}
          >
            <Inspections />
          </Feature>
        ),
      },
      {
        path: "easypay_confirmation",
        loader() {
          const { event } = urlSearchParamsObject();
          if (event === "signing_complete") {
            return redirect("/my/documents?documents_signed=true");
          } else {
            return redirect("/my/documents");
          }
        },
        element: null,
      },
      {
        path: "settings/*",
        element: <Settings />,
        children: [
          { path: "*", element: <Navigate to={""} replace /> },
          {
            path: "security",
            element: <Security />,
          },
          {
            path: "policies?",
            element: <Policies />,
          },
          {
            path: "contact",
            element: <Contact />,
          },
          {
            path: "paperless",
            element: <Paperless />,
          },
          {
            path: "payment",
            element: <Payment />,
          },
          {
            path: "property-services",
            element: <Offers />,
          },
        ],
      },
      {
        path: "property-services",
        element: <Offer />,
        children: [
          {
            path: "ting-offer",
            element: <TingFlow />,
            children: [
              {
                index: true,
                element: <TingLanding />,
              },
              {
                path: "policy-confirmation",
                element: <PolicyConfirmationTing />,
              },
              {
                path: "contact-information",
                element: <ContactInformationTing />,
              },
              {
                path: "contact-information-confirmation",
                element: <ContactInformationConfirmationTing />,
              },
              {
                path: "*",
                loader: () => redirect("/my/property-services/ting-offer"),
              },
            ],
          },
          {
            path: "wind-defense",
            element: <WindDefenseFlow />,
            children: [
              {
                index: true,
                element: <ShingleResealLanding />,
              },
              {
                path: "policy-confirmation",
                element: <PolicyConfirmation />,
              },
              {
                path: "contact-information",
                element: <ContactInformation />,
              },
              {
                path: "waiver-sagesure",
                element: <WaiverSageSure />,
              },
              {
                path: "waiver-liftlock",
                element: <WaiverLiftlock />,
              },
              {
                path: "contact-information-confirmation",
                element: <ContactInformationConfirmation />,
              },
              {
                path: "*",
                loader: () => redirect(`/my/property-services/wind-defense`),
              },
            ],
          },
          {
            path: "*",
            loader: () => redirect("/my/overview"),
          },
        ],
      },
      {
        path: "property-services/ting-offer/registered",
        element: <TingEnrolled />,
      },
      {
        path: "property-services/wind-defense/registered",
        element: <Enrolled />,
      },
      {
        path: "property-services/ineligible",
        element: <IneligibleOffer />,
      },
    ],
  },
];

// Router root
const routes = [
  {
    path: "/",
    loader: rootLoader,
    element: <Main />,
    errorElement: <FullPageError />,
    children: [...publicPages, ...privatePages],
  },
  {
    path: "*",
    loader({ request }) {
      trackEvent("Redirecting to Overview (404)", {
        requestedUrl: request.url,
      });
      return redirect("/");
    },
    element: null,
  },
];

async function rootLoader() {
  const { origin, to } = urlSearchParamsObject();
  const mixpanelProps: { version: string; emailOrigin?: string } = {
    version: __VERSION__,
  };
  if (origin) {
    mixpanelProps.emailOrigin = origin;
  } else if (to) {
    const redirectSearchParams = separateRedirectParams();
    const { origin: redirectOrigin } =
      urlSearchParamsObject(redirectSearchParams);
    if (redirectOrigin) {
      mixpanelProps.emailOrigin = origin;
    }
  }
  setMixpanelSuperProperties(mixpanelProps);
  return null;
}

async function privateLoader({ request }) {
  // if not logged in, redirect to home.
  if (!isLoggedIn()) {
    const url = new URL(request.url);
    url.searchParams.append("to", url.pathname);
    url.pathname = "/auth/login";
    return redirectDocument(url.toString());
  }
  return {};
}

// Create static router
export const router = () => wrappedBrowserRouter(routes);
